import * as React from "react";
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Button,
  Dropdown,
  Option,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { FieldType, PlacedField, Recipient } from "../types";
import {
  FIELD_TYPE_CONFIGS,
  RECIPIENT_COLORS,
  insertFieldContentControl,
  removeFieldContentControl,
} from "../services/contentControls";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "12px",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  label: {
    marginBottom: "-6px",
  },
  placeRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  fieldList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  fieldCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: tokens.borderRadiusSmall,
    borderLeftWidth: "3px",
    borderLeftStyle: "solid",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  fieldInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  fieldMeta: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  recipientDot: {
    display: "inline-block",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  count: {
    color: tokens.colorNeutralForeground3,
  },
  emptyState: {
    textAlign: "center" as const,
    padding: "16px 12px",
    color: tokens.colorNeutralForeground3,
  },
});

const FIELD_TYPES: FieldType[] = [
  "signature",
  "initials",
  "approval_stamp",
  "full_name",
  "email",
  "date_signed",
  "text",
  "date",
  "checkbox",
];

interface Props {
  recipients: Recipient[];
  placedFields: PlacedField[];
  onFieldsChanged: (fields: PlacedField[]) => void;
  onNext: () => void;
  onBack: () => void;
}

let fieldCounter = 0;

const FieldPlacement: React.FC<Props> = ({ recipients, placedFields, onFieldsChanged, onNext, onBack }) => {
  const styles = useStyles();
  const [selectedRecipientIndex, setSelectedRecipientIndex] = React.useState(0);
  const [selectedFieldType, setSelectedFieldType] = React.useState<FieldType>("signature");
  const [placing, setPlacing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handlePlaceField = async () => {
    setPlacing(true);
    setError(null);

    try {
      const id = `field_${Date.now()}_${fieldCounter++}`;
      const name = `${selectedFieldType}_${fieldCounter}`;
      const tag = `ddsign-field::${id}`;

      const newField: PlacedField = {
        id,
        type: selectedFieldType,
        name,
        recipientIndex: selectedRecipientIndex,
        contentControlTag: tag,
      };

      await insertFieldContentControl(newField, recipients[selectedRecipientIndex].name);
      onFieldsChanged([...placedFields, newField]);
    } catch (err: any) {
      setError(err.message || "Failed to place field. Make sure your cursor is in the document.");
    } finally {
      setPlacing(false);
    }
  };

  const handleRemoveField = async (fieldId: string) => {
    try {
      await removeFieldContentControl(fieldId);
    } catch {
      // Content control may already be deleted by user — continue
    }
    onFieldsChanged(placedFields.filter((f) => f.id !== fieldId));
  };

  return (
    <div className={styles.container}>
      <div>
        <Title2>Place Fields</Title2>
        <Body1 className={styles.subtitle} block>
          Position your cursor in the document, then place a field.
        </Body1>
      </div>

      <div className={styles.controls}>
        <Caption1 className={styles.label} weight="semibold">Recipient</Caption1>
        <Dropdown
          size="small"
          value={recipients[selectedRecipientIndex]?.name || ""}
          selectedOptions={[String(selectedRecipientIndex)]}
          onOptionSelect={(_, data) => setSelectedRecipientIndex(Number(data.optionValue))}
        >
          {recipients.map((r, i) => (
            <Option key={i} value={String(i)} text={r.name}>
              <span className={styles.recipientDot} style={{ backgroundColor: RECIPIENT_COLORS[i % RECIPIENT_COLORS.length] }} />
              {r.name}
            </Option>
          ))}
        </Dropdown>

        <Caption1 className={styles.label} weight="semibold">Field Type</Caption1>
        <Dropdown
          size="small"
          value={FIELD_TYPE_CONFIGS[selectedFieldType].label}
          selectedOptions={[selectedFieldType]}
          onOptionSelect={(_, data) => setSelectedFieldType(data.optionValue as FieldType)}
        >
          {FIELD_TYPES.map((type) => (
            <Option key={type} value={type} text={FIELD_TYPE_CONFIGS[type].label}>
              {FIELD_TYPE_CONFIGS[type].label}
            </Option>
          ))}
        </Dropdown>

        <Button
          appearance="primary"
          size="small"
          onClick={handlePlaceField}
          disabled={placing}
        >
          {placing ? "Placing..." : "Place at Cursor"}
        </Button>
      </div>

      {error && <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Caption1>}

      <div>
        <div className={styles.sectionHeader}>
          <Caption1 weight="semibold">Fields</Caption1>
          <Caption1 className={styles.count}>{placedFields.length} placed</Caption1>
        </div>
        {placedFields.length === 0 ? (
          <Body2 className={styles.emptyState}>No fields placed yet</Body2>
        ) : (
          <div className={styles.fieldList}>
            {placedFields.map((field) => {
              const color = RECIPIENT_COLORS[field.recipientIndex % RECIPIENT_COLORS.length];
              const recipient = recipients[field.recipientIndex];
              return (
                <div
                  key={field.id}
                  className={styles.fieldCard}
                  style={{ borderLeftColor: color }}
                >
                  <div className={styles.fieldInfo}>
                    <span className={styles.recipientDot} style={{ backgroundColor: color }} />
                    <div className={styles.fieldMeta}>
                      <Body2>{FIELD_TYPE_CONFIGS[field.type].label}</Body2>
                      <Caption1 className={styles.count}>{recipient?.name}</Caption1>
                    </div>
                  </div>
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => handleRemoveField(field.id)}
                  >
                    Remove
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <Button appearance="secondary" size="small" onClick={onBack}>
          Back
        </Button>
        <Button appearance="primary" size="small" onClick={onNext} disabled={placedFields.length === 0}>
          Continue to Review
        </Button>
      </div>
    </div>
  );
};

export default FieldPlacement;
